import { useState, type FormEvent } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/features/auth/AuthProvider";
import { BrandMark } from "@/components/BrandMark";

const loginSearch = z.object({
  next: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: loginSearch,
  component: LoginPage,
});

type Step = "enter-email" | "enter-code";

function LoginPage() {
  const { next } = Route.useSearch();
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("enter-email");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !email.includes("@")) {
      setError("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/login", { email: email.trim().toLowerCase(), next });
      setStep("enter-code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't send the code.");
    } finally {
      setSubmitting(false);
    }
  }

  // Dev-only shortcut: skips the email round-trip via the localhost-gated
  // /api/auth/dev-login endpoint. Tree-shaken out of production builds.
  async function devLogin() {
    setError(null);
    if (!email || !email.includes("@")) {
      setError("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/dev-login", { email: email.trim().toLowerCase() });
      const user = await refresh();
      if (user) navigate({ to: next ?? "/" });
      else setError("Dev login didn't establish a session.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Dev login failed.");
    } finally {
      setSubmitting(false);
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Enter the 6-digit code.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/api/auth/verify", {
        email: email.trim().toLowerCase(),
        token: token.trim(),
        next,
      });
      const user = await refresh();
      if (user) {
        navigate({ to: next ?? "/" });
      } else {
        setError("Session not established — try again.");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid or expired code.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="brand-stack">
        <span className="brand-mark-lg" aria-hidden>
          <BrandMark />
        </span>
        <span className="eyebrow" aria-hidden>welcome back</span>
      </div>

      <div className="panel" style={{ padding: "32px 30px 28px" }}>
        <h1>Sign in</h1>
        <p className="lede">We&apos;ll email you a magic link and a 6-digit code — use either.</p>

        {step === "enter-email" ? (
          <form onSubmit={sendCode}>
            <div className="field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="you@bookboost.io"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error ? "true" : undefined}
                aria-describedby={error ? "email-error" : undefined}
              />
            </div>
            {error && (
              <div className="inline-err" id="email-error" role="alert">
                <ErrorIcon />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              className="primary"
              disabled={submitting}
              style={{ marginTop: 16, width: "100%" }}
            >
              {submitting ? "Sending…" : "Email me a sign-in link"}
            </button>
            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={devLogin}
                disabled={submitting}
                style={{ marginTop: 10, width: "100%" }}
                title="Local dev only — bypasses email via the localhost-gated dev-login endpoint"
              >
                Dev login (localhost only)
              </button>
            )}
          </form>
        ) : (
          <>
            <div className="sent-note">
              <svg className="check" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M5 9.5l2.5 2.5L13 6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>Check <strong>{email}</strong>, then click the link or enter the code below.</div>
            </div>
            <form onSubmit={verifyCode}>
              <div className="field">
                <label htmlFor="token">Verification code</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="token"
                    className="code-input"
                    name="token"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    autoFocus
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="primary" disabled={submitting}>
                    {submitting ? "Verifying…" : "Verify"}
                  </button>
                </div>
              </div>
              {error && (
                <div className="inline-err" role="alert">
                  <ErrorIcon />
                  <span>{error}</span>
                </div>
              )}
            </form>
          </>
        )}
      </div>

      <p className="fine-print">
        Access is limited to Bookboost commercial, admin, and finance team members.<br />
        If your email isn&apos;t recognised, contact Dylan.
      </p>
    </div>
  );
}

function ErrorIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
      <path d="M7 4v3.5M7 9.2v.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
